import React from 'react';
import { AlertCircle, CheckCircle2, Search } from 'lucide-react';

export const PageLayout = ({ children, className = '' }) => (
  <div className={`flex min-h-full flex-col gap-6 px-1 sm:px-2 lg:px-4 ${className}`.trim()}>{children}</div>
);

export const PageHeader = ({ title, description, actions }) => (
  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
    <div className="page-heading">
      <h1 className="text-3xl font-semibold tracking-tight text-slate-800">{title}</h1>
      {description ? <p className="mt-2 text-sm text-gray-500">{description}</p> : null}
    </div>
    {actions ? <div className="flex flex-wrap gap-3">{actions}</div> : null}
  </div>
);

export const SummaryGrid = ({ children, columns = 'md:grid-cols-3' }) => (
  <div className={`grid grid-cols-1 gap-2 ${columns}`.trim()}>{children}</div>
);

export const SummaryCard = ({ title, value, icon, color = 'blue', active = false, onClick, subtitle }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-500',
    green: 'bg-green-50 text-green-500',
    purple: 'bg-purple-50 text-purple-500',
    red: 'bg-red-50 text-red-500',
    amber: 'bg-amber-50 text-amber-500',
    emerald: 'bg-emerald-50 text-emerald-500',
  };

  const sharedClassName = `summary-card flex w-full items-center justify-between rounded-2xl border bg-white px-4 py-3 text-left shadow-sm transition ${
    active
      ? 'summary-card-active border-condo-teal shadow-md'
      : 'border-slate-200 hover:shadow-md'
  }`;

  const content = (
    <>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{title}</p>
        <p className="mt-1 text-2xl font-semibold tracking-tight leading-none text-slate-800">{value}</p>
        {subtitle ? <p className="mt-1 text-xs text-slate-400">{subtitle}</p> : null}
      </div>
      {icon ? <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${colors[color] || colors.blue}`}>{icon}</div> : null}
    </>
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={sharedClassName}>
        {content}
      </button>
    );
  }

  return <div className={sharedClassName}>{content}</div>;
};

export const SearchField = ({ value, onChange, placeholder }) => (
  <div className="relative">
    <Search className="absolute left-3 top-3.5 text-gray-400" size={20} />
    <input
      type="text"
      placeholder={placeholder}
      className="block w-full rounded-xl border border-gray-200 bg-white py-3 pl-10 pr-3 shadow-sm outline-none transition-all focus:ring-2 focus:ring-condo-teal"
      value={value}
      onChange={onChange}
    />
  </div>
);

export const FeedbackBanner = ({ type = 'info', message, onClose }) => {
  if (!message) {
    return null;
  }

  const styles = {
    success: 'border-green-200 bg-green-50 text-green-800',
    error: 'border-red-200 bg-red-50 text-red-800',
    info: 'border-sky-200 bg-sky-50 text-sky-800',
  };

  const Icon = type === 'success' ? CheckCircle2 : AlertCircle;

  return (
    <div className={`flex items-start justify-between gap-3 rounded-2xl border px-4 py-3 text-sm ${styles[type] || styles.info}`}>
      <div className="flex items-start gap-3">
        <Icon size={18} className="mt-0.5 shrink-0" />
        <span>{message}</span>
      </div>
      {onClose ? (
        <button type="button" onClick={onClose} className="font-medium opacity-70 transition hover:opacity-100">
          Fechar
        </button>
      ) : null}
    </div>
  );
};

export const LoadingState = ({ message = 'Carregando dados...' }) => (
  <div className="flex min-h-[220px] items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-10 text-center text-sm font-medium text-gray-500 shadow-sm">
    {message}
  </div>
);

export const EmptyState = ({ message }) => (
  <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-16 text-center text-sm italic text-gray-400 shadow-sm">
    {message}
  </div>
);
